import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { Text } from '../text';

type LabeledTextProps = {
  /**
   * ラベルのテキスト
   */
  label: string;

  /**
   * 表示するテキスト内容
   */
  children: ReactNode;

  /**
   * グリッド内でのカラムスパン数（デフォルト: 1）
   */
  colSpan?: number;

  /**
   * テキストの改行を維持するか（デフォルト: false）
   */
  preserveWhitespace?: boolean;

  /**
   * ラベル用の追加クラス名
   */
  labelClassName?: string;

  /**
   * テキスト用の追加クラス名
   */
  textClassName?: string;

  /**
   * 全体のコンテナ用の追加クラス名
   */
  className?: string;
};

/**
 * ラベルとテキストを表示する汎用コンポーネント
 *
 * グリッドレイアウト内でラベル付きのテキスト情報を表示する際に使用します。
 *
 * @example
 * ```tsx
 * // 基本的な使い方
 * <LabeledText label="事業年度">
 *   2024
 * </LabeledText>
 *
 * // 改行を維持する場合
 * <LabeledText label="予算概要" preserveWhitespace>
 *   {project.budget_description}
 * </LabeledText>
 *
 * // カラムスパンを指定
 * <LabeledText label="事業概要" colSpan={2}>
 *   {project.project_description}
 * </LabeledText>
 * ```
 */
export const LabeledText = ({
  label,
  children,
  colSpan = 1,
  preserveWhitespace = false,
  labelClassName,
  textClassName,
  className,
}: LabeledTextProps) => {
  // children が文字列・数値・空値（null / undefined）の場合は Text コンポーネントでラップする。
  // 空値は Text の '--' フォールバックに委ねて表示を統一する。
  // それ以外（ReactNode 要素の場合）は div でラップする。
  const isTextContent =
    children == null || typeof children === 'string' || typeof children === 'number';

  return (
    <div
      className={cn('flex flex-col gap-2', className)}
      style={colSpan > 1 ? { gridColumn: `span ${colSpan}` } : undefined}
    >
      <Text variant="label" as="label" className={labelClassName}>
        {label}
      </Text>
      {isTextContent ? (
        <Text
          variant="body"
          className={cn(preserveWhitespace && 'whitespace-pre-wrap', textClassName)}
        >
          {children}
        </Text>
      ) : (
        <div className={cn('text-sm', preserveWhitespace && 'whitespace-pre-wrap', textClassName)}>
          {children}
        </div>
      )}
    </div>
  );
};
